import { IProjekt } from './projekt.model';
import { Aufgabe, ValidationMessage } from './aufgabe.model';
import { INPUT_LIMITS } from '../constants/input-limits';
import { ValidationFinding, ValidationTaskRef } from './validation-finding';

// NP20-828: Zentrale Pruefung fuer das Projekte-Badge. Consultant-Section-Header
// und Projekt-Karten teilen sich denselben Filter, damit die beiden Anzeigen nicht
// still auseinanderlaufen.
//
// NP20-1093: Gerechnet wird nur noch einmal — die Pruefung liefert die Fundstellen,
// jede Zahl in der Oberflaeche ist deren Anzahl.

const REQUIRED_REASON = 'general.errors.required';

interface ValidationReason {
  key: string;
  params?: Record<string, unknown>;
}

/** Zahl der Projektfelder, die `listProjectFieldFindings` betrachtet. */
const PROJECT_FIELD_CHECKS = 10;
/** Zahl der blockierenden Felder je Aufgabe: caption, details, skills. */
const TASK_FIELD_CHECKS = 3;

/**
 * NP20-1192: Fundstellen und die Zahl der dabei geprueften Stellen aus einem Durchlauf.
 * Der Vervollstaendigungsgrad der Beraterübersicht braucht beides — ohne die zweite
 * Zahl muesste er die Zaehlbasis der Projekte nachbilden und damit ein zweites
 * Regelwerk fuehren.
 */
export interface ProjectValidation {
  findings: ValidationFinding[];
  checked: number;
}

export function listProjectValidation(
  projekt: IProjekt | null | undefined,
): ProjectValidation {
  if (!projekt) return { findings: [], checked: 0 };
  const tasks = projekt.tasks ?? [];
  return {
    findings: [
      ...listProjectFieldFindings(projekt),
      ...listTaskSectionFindings(projekt),
    ],
    // Die leere Aufgabenliste ist genau eine geprüfte Stelle — das BE erzwingt
    // `@NotEmpty tasks`, pro-Task-Felder kann es dafuer nicht geben.
    checked:
      PROJECT_FIELD_CHECKS +
      (tasks.length === 0 ? 1 : TASK_FIELD_CHECKS * tasks.length),
  };
}

/**
 * Alle Stellen an einem Projekt, die das Speichern blockieren: Projektfelder
 * gefolgt von der Aufgaben-Section. Die Badge-Zahl ist die Laenge dieser Liste.
 *
 * Zwei Regeln, an denen sich alles hier ausrichtet:
 *
 * * **Ein blockierendes Feld = genau eine Fundstelle**, auch wenn es an mehreren
 *   Grenzen gleichzeitig scheitert.
 * * **Nur was das Speichern verhindert**, steht drin. Die Feldliste spiegelt
 *   deshalb die Validatoren aus `project.component.html` (`form.invalid ||
 *   !hasIndustries || !hasTasks`): required, minlength, maxlength und die
 *   `min`-Grenzen der Zahlenfelder. Reine Hinweise (Skills-Meldungen, alles mit
 *   severity `warn`/`info`) bleiben aussen vor.
 */
export function listProjectFindings(
  projekt: IProjekt | null | undefined,
): ValidationFinding[] {
  return listProjectValidation(projekt).findings;
}

/**
 * Fehler in der Aufgaben-Section — dieselbe Quelle, die die Projekt-Karte im Badge
 * und der Aufgaben-Section-Hinweis im Bearbeiten-Modus nutzen (NP20-893):
 *
 * * leere `tasks`-Liste zaehlt als **ein** Fehler auf Projekt-Ebene — das BE
 *   erzwingt `@NotEmpty tasks` (NP20-892), pro-Task-Fehler kann es fuer eine
 *   leere Liste nicht geben.
 * * andernfalls ein Fehler pro blockierendem Task-Feld (`caption`, `details`,
 *   `skills`); die Hinweise aus `validateSkills` zu zu wenigen oder zu vielen
 *   Faehigkeiten bleiben warn/info und zaehlen daher nicht.
 */
export function listTaskSectionFindings(
  projekt: IProjekt | null | undefined,
): ValidationFinding[] {
  if (!projekt) return [];
  const tasks = projekt.tasks ?? [];
  if (tasks.length === 0) {
    return [
      {
        section: 'tasks',
        field: 'tasks',
        labelKey: 'project.tasksLabel',
        reasonKey: 'project.error.tasksRequired',
      },
    ];
  }

  const findings: ValidationFinding[] = [];
  tasks.forEach((rawTask, index) => {
    // Tasks aus der API kommen als Plain Objects (nicht als Aufgabe-Instanz),
    // daher immer via Konstruktor rehydrieren, damit validate() verfuegbar ist.
    const task = new Aufgabe(
      rawTask.caption,
      rawTask.details,
      rawTask.skills,
      rawTask.uuid,
    );
    const { caption, details, skills } = task.validate();
    const taskRef = toTaskRef(rawTask.caption, index);
    addTaskFinding(findings, caption, 'caption', 'project.title', taskRef);
    addTaskFinding(
      findings,
      details,
      'details',
      'project.description',
      taskRef,
    );
    addTaskFinding(findings, skills, 'skills', 'project.skills', taskRef);
  });
  return findings;
}

function toTaskRef(caption: unknown, index: number): ValidationTaskRef {
  const trimmed = typeof caption === 'string' ? caption.trim() : '';
  return trimmed.length > 0 ? { index, caption: trimmed } : { index };
}

/** Hoechstens eine Fundstelle pro Feld — der erste blockierende Grund zaehlt. */
function addTaskFinding(
  findings: ValidationFinding[],
  messages: ValidationMessage[],
  field: string,
  labelKey: string,
  task: ValidationTaskRef,
): void {
  const blocking = messages.find(isBlocking);
  if (!blocking) return;
  findings.push({
    section: 'tasks',
    field,
    labelKey,
    reasonKey: blocking.key,
    ...(blocking.params ? { reasonParams: blocking.params } : {}),
    task,
  });
}

function isBlocking(message: ValidationMessage): boolean {
  return message.severity === 'error';
}

function listProjectFieldFindings(projekt: IProjekt): ValidationFinding[] {
  const findings: ValidationFinding[] = [];
  const add = (
    field: string,
    labelKey: string,
    reason: ValidationReason | null,
  ): void => {
    if (!reason) return;
    findings.push({
      section: 'projects',
      field,
      labelKey,
      reasonKey: reason.key,
      ...(reason.params ? { reasonParams: reason.params } : {}),
    });
  };

  add(
    'beginn',
    'project.projectPeriodLabel',
    projekt.beginn ? null : { key: REQUIRED_REASON },
  );
  add(
    'projektname',
    'project.projectNameLabel',
    textReason(projekt.projektname, {
      required: true,
      max: INPUT_LIMITS.SHORT_LABEL,
    }),
  );
  add(
    'kunde',
    'project.projectCompanyLabel',
    textReason(projekt.kunde, {
      required: true,
      max: INPUT_LIMITS.SHORT_LABEL,
    }),
  );
  add(
    'stelle',
    'project.projectPositionLabel',
    textReason(projekt.stelle, {
      required: true,
      max: INPUT_LIMITS.SHORT_LABEL,
    }),
  );
  add(
    'beschreibung',
    'project.projectDescription',
    textReason(projekt.beschreibung, {
      required: true,
      min: INPUT_LIMITS.PROJECT_DESCRIPTION_MIN,
      max: INPUT_LIMITS.PROJECT_DESCRIPTION,
    }),
  );
  add(
    'workLocation',
    'project.projectDeploymentLocation',
    textReason(projekt.workLocation, { max: INPUT_LIMITS.LOCATION }),
  );
  add(
    'teamSize',
    'project.projectSize',
    numberReason(projekt.teamSize, {
      required: true,
      min: 1,
      minReasonKey: 'project.error.teamSizePositive',
    }),
  );
  add(
    'numberTester',
    'project.projectTester',
    numberReason(projekt.numberTester, { required: true, min: 0 }),
  );
  add(
    'projectSize',
    'project.projectTotalSize',
    numberReason(projekt.projectSize, { min: 0 }),
  );
  add(
    'branchNames',
    'project.projectIndustries',
    (projekt.branchNames?.length ?? 0) === 0
      ? { key: 'project.error.industriesRequired' }
      : null,
  );
  return findings;
}

/**
 * Spiegelt required/minlength/maxlength eines Textfeldes.
 *
 * `required` und `min` rechnen mit der getrimmten Laenge — dieselbe Regel, die
 * `appTrimmedLength` an den Textfeldern des Formulars anwendet. `max` misst die
 * Rohlaenge, weil die Felder Angulars ungetrimmtes `maxlength` tragen. Beides
 * zusammen misst dieselbe Laenge wie das Formular, so dass es den Zustand
 * "Badge meldet einen Fehler, Speichern-Button bleibt aktiv" (und die Umkehrung)
 * nicht geben kann.
 */
function textReason(
  value: unknown,
  rules: { required?: boolean; min?: number; max?: number },
): ValidationReason | null {
  if (typeof value !== 'string' || value.length === 0) {
    return rules.required ? { key: REQUIRED_REASON } : null;
  }
  const trimmed = value.trim();
  if (rules.required && trimmed.length === 0) return { key: REQUIRED_REASON };
  if (rules.min !== undefined && trimmed.length < rules.min) {
    return { key: 'general.validation.minLength', params: { min: rules.min } };
  }
  if (rules.max !== undefined && value.length > rules.max) {
    return { key: 'general.validation.maxLength', params: { max: rules.max } };
  }
  return null;
}

/** Spiegelt required und die `min`-Grenze eines Zahlenfeldes; 0 ist kein leerer Wert. */
function numberReason(
  value: unknown,
  rules: { required?: boolean; min?: number; minReasonKey?: string },
): ValidationReason | null {
  if (value === null || value === undefined || value === '') {
    return rules.required ? { key: REQUIRED_REASON } : null;
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return { key: REQUIRED_REASON };
  if (rules.min !== undefined && numeric < rules.min) {
    return rules.minReasonKey
      ? { key: rules.minReasonKey }
      : { key: 'general.validation.minValue', params: { min: rules.min } };
  }
  return null;
}
